import {isTestAccount, type TestAccountInfo} from "@buildwithflux/accounts-logic";
import {
    type SearchIndexAlias,
    TYPESENSE_PARTS_COLLECTION_NAME,
    TYPESENSE_PROJECTS_COLLECTION_NAME,
} from "@buildwithflux/constants";
import {PROJECT_CARD_ROW_GAP_PX, UNIFORM_PROJECT_CARD_ROW_HEIGHT_PX} from "@buildwithflux/project-cards";
import {
    UNSCOPED_SEARCH_RESULT_LIMIT,
    UNSCOPED_SEARCH_RESULT_LIMIT_LABEL,
    capUnscopedTotalHits,
    clampUnscopedSearchPage,
    isUnscopedTotalCapped,
    maxUnscopedSearchPage,
} from "@buildwithflux/search";
import {type DocumentDataForUrlRouting, getDocumentUrl, Routes} from "@buildwithflux/url";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import type {SxProps, Theme} from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useCallback, useEffect, useState} from "react";
import {
    useSearchParams,
    useNavigate,
    useLoaderData,
    useLocation,
    Link as RouterLink,
    type LoaderFunctionArgs,
    type MetaArgs,
    type MetaDescriptor,
} from "react-router";
import type {Client} from "typesense";

import awesome from "../assets/awesome.png";
import FluxLogo from "../assets/flux_logo.svg";
import {useViewer} from "../context/ViewerContext";
import {useAccountsViewClientInitialization} from "../hooks/useAccountsViewClient";
import {useDocumentTitle} from "../hooks/useDocumentTitle";
import {useSearchListingConfig} from "../hooks/useSearchListingConfig";
import {useTypesenseClientInitialization} from "../hooks/useTypesenseClient";
import {APP_DESCRIPTION, siteMetaDescriptors, toCanonicalUrl} from "../seo";
import type {SiteSearchLoadContext} from "../server/loaderContext";
import {typesenseSearch} from "../server/typesenseRest";
import {selectTypesenseSearchValue} from "../typesenseCredentials";
import {getPreviewImageUrl} from "../utils/imageUrl";
import {getSearchResultsIndexing} from "../utils/searchIndexing";

import AccountCard, {type AccountHit} from "./cards/AccountCard";
import ProjectCard, {type ProjectHit} from "./cards/ProjectCard";
import UnifiedProjectCardSkeleton from "./cards/UnifiedProjectCardSkeleton";
import {createSearchUrl} from "./search/createSearchUrl";
import {
    createClientSearchKey,
    hasSearchClientFailed,
    shouldShowInitialSearchSpinner,
    shouldUseSearchFallback,
} from "./search/initialSearchLoading";
import {useHideNativeScrollbar} from "./search/SearchListingScrollbar";
import {formatSearchSectionCount, getSearchFallbackType, SEARCH_INDEX_ALIASES} from "./search/searchSectionFallback";
import SearchTip from "./search/SearchTip";
import {toAccountHit} from "./search/toAccountHit";
import VirtualizedSearchResults from "./search/VirtualizedSearchResults";

const PER_PAGE = 25;

const landingOuterSx: SxProps<Theme> = (theme) => ({
    width: "100%",
    flexGrow: 1,
    minHeight: "calc(100vh - 48px)",
    boxSizing: "border-box",
    textAlign: "center",
    display: "flex",
    paddingTop: "15%",
    backgroundImage: `url(${awesome})`,
    backgroundPosition: "bottom",
    backgroundSize: "624px",
    [theme.breakpoints.down("sm")]: {
        backgroundSize: "524px",
    },
    backgroundRepeat: "no-repeat",
});

function renderUnifiedProjectSkeleton() {
    return <UnifiedProjectCardSkeleton />;
}

type SearchRow = {kind: "account"; data: AccountHit} | {kind: "project"; data: ProjectHit};

export function meta({data, location}: MetaArgs): MetaDescriptor[] {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get("q") ?? "";
    const queryType = (searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const pageNum = parseInt(searchParams.get("page") ?? "1", 10) || 1;

    if (!query) {
        return [
            {title: "Flux Search"},
            {name: "description", content: APP_DESCRIPTION},
            {property: "og:title", content: "Flux Search"},
            {property: "og:description", content: APP_DESCRIPTION},
            {property: "og:type", content: "website"},
            {property: "og:url", content: toCanonicalUrl(Routes.SEARCH)},
            {name: "twitter:title", content: "Flux Search"},
            {name: "twitter:description", content: APP_DESCRIPTION},
            ...siteMetaDescriptors(),
        ];
    }

    const title = `Flux Search Results for "${query}"`;
    const absoluteUrl = toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&type=${queryType}`);
    const indexing = getSearchResultsIndexing(query, queryType, pageNum);
    const canonicalUrl = toCanonicalUrl(indexing.canonicalPath);
    const loaderData = data as SearchLoaderData | null | undefined;
    const totalHits = loaderData && loaderData.query === query ? loaderData.totalHits : 0;
    const totalPages = Math.ceil(totalHits / PER_PAGE);

    const descriptors: MetaDescriptor[] = [
        {title},
        {name: "description", content: APP_DESCRIPTION},
        {tagName: "link", rel: "canonical", href: canonicalUrl},
        {property: "og:title", content: title},
        {property: "og:description", content: APP_DESCRIPTION},
        {property: "og:type", content: "website"},
        {property: "og:url", content: canonicalUrl},
        {name: "twitter:title", content: title},
        {name: "twitter:description", content: APP_DESCRIPTION},
        ...siteMetaDescriptors(),
    ];

    if (indexing.noindex) {
        descriptors.push({name: "robots", content: "noindex, follow"});
    }

    if (pageNum < totalPages) {
        descriptors.push({
            tagName: "link",
            rel: "next",
            href: toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&page=${pageNum + 1}`),
        });
    }

    if (totalHits > 0) {
        descriptors.push({
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: title,
                description: `Search results for "${query}" in ${humanizeType(queryType)} on Flux`,
                url: absoluteUrl,
                numberOfItems: totalHits,
            },
        });
    }

    return descriptors;
}

function isAccountQuery(queryType: SearchIndexAlias): boolean {
    return queryType === "users" || queryType === "orgs";
}

function humanizeType(queryType: SearchIndexAlias): string {
    switch (queryType) {
        case "projects":
            return "Projects";
        case "components":
            return "Components";
        case "users":
            return "Users";
        case "orgs":
            return "Organizations";
    }
}

function getProjectCollectionName(queryType: "projects" | "components"): string {
    return queryType === "projects" ? TYPESENSE_PROJECTS_COLLECTION_NAME : TYPESENSE_PARTS_COLLECTION_NAME;
}

function getProjectQueryBy(queryType: "projects" | "components"): string {
    return queryType === "projects" ? "uid,name,description,slug" : "uid,name,nameSuffixes,description";
}

function getProjectSortBy(queryType: "projects" | "components"): string {
    return queryType === "projects"
        ? "star_count:desc,_text_match:desc,comment_count:desc"
        : "_text_match:desc,contentScore:desc,updated_at:desc";
}

function getProjectFilterBy(queryType: "projects" | "components"): string {
    return queryType === "projects" ? "test_data:false&&is_published:false" : "test_data:false";
}

function num(value: unknown): number | undefined {
    return typeof value === "number" ? value : undefined;
}

function str(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function bool(value: unknown): boolean {
    return value === true;
}

function toProjectHit(doc: {[key: string]: unknown}, queryType: "projects" | "components"): ProjectHit | null {
    const uid = str(doc.uid ?? doc.objectID);
    const ownerHandle = str(doc.owner_handle ?? doc.handle);
    const slug = str(doc.slug);

    let url: string;
    const projectUrl = str(doc.project_url);
    if (projectUrl) {
        // Canonical slug~short_id URL denormalized onto the search record by the backend
        url = projectUrl;
    } else if (queryType === "projects" && uid && slug && ownerHandle) {
        // Projects collection carries slug/short_id, so build the canonical slug~short_id URL
        url = getDocumentUrl(doc as DocumentDataForUrlRouting);
    } else {
        // No canonical URL available (e.g. a part whose project_url has not been
        // backfilled). Omit the hit rather than emit a non-routable
        // /projects/<uid> link, which 404s (a part uid is not a project uid).
        return null;
    }

    const contributorUids = Array.isArray(doc.contributor_uids)
        ? (doc.contributor_uids.filter((u) => typeof u === "string") as string[])
        : [];

    return {
        uid,
        name: str(doc.name),
        description: str(doc.description),
        url,
        thumbUrl: getPreviewImageUrl(str(doc.preview_image) || undefined, 124, doc),
        ownerUid: str(doc.owner_uid),
        ownerHandle,
        contributorUids,
        usageCount: num(doc.usage_count) ?? num(doc.use_count),
        commentCount: num(doc.comment_count),
        starCount: num(doc.star_count),
        updatedAt: num(doc.updated_at),
    };
}

/** Production filters test accounts at the card level with `isTestAccount("loose")`. */
function keepAccount(doc: {[key: string]: unknown}, queryType: "users" | "orgs"): boolean {
    if (queryType === "users") {
        return !isTestAccount({...doc, isAnonymous: false, is_test: false} as unknown as TestAccountInfo, "loose");
    }
    return !isTestAccount(doc as TestAccountInfo, "loose");
}

async function executeProjectSearch(
    client: Client,
    queryType: "projects" | "components",
    query: string,
    page: number,
): Promise<{rows: SearchRow[]; totalHits: number; totalHitsCapped: boolean}> {
    if (page > maxUnscopedSearchPage(PER_PAGE)) {
        return {rows: [], totalHits: 0, totalHitsCapped: false};
    }
    const response = await client
        .collections(getProjectCollectionName(queryType))
        .documents()
        .search({
            q: query,
            query_by: getProjectQueryBy(queryType),
            sort_by: getProjectSortBy(queryType),
            filter_by: getProjectFilterBy(queryType),
            per_page: PER_PAGE,
            page: clampUnscopedSearchPage(page, PER_PAGE),
            limit_hits: UNSCOPED_SEARCH_RESULT_LIMIT,
            min_len_1typo: 5,
            min_len_2typo: 8,
        });

    const rows = (response.hits ?? []).flatMap<SearchRow>((hit) => {
        const data = toProjectHit(hit.document as {[key: string]: unknown}, queryType);
        return data ? [{kind: "project", data}] : [];
    });

    return {
        rows,
        totalHits: capUnscopedTotalHits(response.found),
        totalHitsCapped: isUnscopedTotalCapped(response.found),
    };
}

async function executeAccountSearch(
    client: Client,
    collection: string,
    queryType: "users" | "orgs",
    query: string,
    page: number,
): Promise<{rows: SearchRow[]; totalHits: number}> {
    const response = await client
        .collections(collection)
        .documents()
        .search({
            q: query,
            query_by: "full_name,handle",
            sort_by: "follower_count:desc,_text_match:desc,star_count:desc",
            filter_by: queryType === "users" ? "accountType:=user" : "accountType:=organization",
            per_page: PER_PAGE,
            page,
            min_len_1typo: 4,
            min_len_2typo: 8,
        });

    const rows = (response.hits ?? [])
        .map((hit) => hit.document as {[key: string]: unknown})
        .filter((doc) => keepAccount(doc, queryType))
        .map<SearchRow>((doc) => ({kind: "account", data: toAccountHit(doc)}));

    return {rows, totalHits: response.found};
}

async function executeSearchCount(
    projectClients: {readonly projects: Client; readonly parts: Client},
    accountsClient: Client,
    accountsCollection: string,
    queryType: SearchIndexAlias,
    query: string,
): Promise<number> {
    if (isAccountQuery(queryType)) {
        const response = await accountsClient
            .collections(accountsCollection)
            .documents()
            .search({
                q: query,
                query_by: "full_name,handle",
                sort_by: "follower_count:desc,_text_match:desc,star_count:desc",
                filter_by: queryType === "users" ? "accountType:=user" : "accountType:=organization",
                include_fields: "id",
                per_page: 1,
                page: 1,
                limit_hits: UNSCOPED_SEARCH_RESULT_LIMIT,
                min_len_1typo: 4,
                min_len_2typo: 8,
            });
        return response.found;
    }

    const projectType = queryType as "projects" | "components";
    const projectClient = selectTypesenseSearchValue(projectClients, projectType);
    const response = await projectClient
        .collections(getProjectCollectionName(projectType))
        .documents()
        .search({
            q: query,
            query_by: getProjectQueryBy(projectType),
            sort_by: getProjectSortBy(projectType),
            filter_by: getProjectFilterBy(projectType),
            include_fields: "uid",
            per_page: 1,
            page: 1,
            limit_hits: UNSCOPED_SEARCH_RESULT_LIMIT,
            min_len_1typo: 5,
            min_len_2typo: 8,
        });
    return response.found;
}

/**
 * Client-side counterpart of `enrichProjectVerified`: batch-resolve owner
 * `verified` flags from the accounts-view collection using the scoped
 * Typesense client, so badges render on the SPA-shell / federated paths too.
 */
async function enrichProjectVerifiedClient(rows: SearchRow[], client: Client, collection: string): Promise<void> {
    const ownerIds = Array.from(
        new Set(
            rows
                .filter((row): row is {kind: "project"; data: ProjectHit} => row.kind === "project")
                .map((row) => row.data.ownerUid)
                .filter(Boolean)
                .map((uid) => (uid.startsWith("usr_") ? uid : `usr_${uid}`)),
        ),
    );
    if (ownerIds.length === 0) return;

    try {
        const response = await client
            .collections(collection)
            .documents()
            .search({
                q: "*",
                query_by: "handle",
                filter_by: `id:[${ownerIds.join(",")}]`,
                include_fields: "id,verified",
                per_page: Math.min(ownerIds.length, 250),
            });

        const verifiedById = new Map<string, boolean>();
        for (const hit of response.hits ?? []) {
            const doc = hit.document as {[key: string]: unknown};
            const id = str(doc.id);
            if (id) verifiedById.set(id, bool(doc.verified));
        }

        for (const row of rows) {
            if (row.kind !== "project") continue;
            const id = row.data.ownerUid.startsWith("usr_") ? row.data.ownerUid : `usr_${row.data.ownerUid}`;
            row.data.ownerVerified = verifiedById.get(id) ?? false;
        }
    } catch (err) {
        // Enrichment is best-effort; never fail the whole render over a badge.
        // eslint-disable-next-line no-console
        console.error("owner verified enrichment failed:", err);
    }
}

export interface SearchLoaderData {
    query: string;
    queryType: SearchIndexAlias;
    page: number;
    rows: SearchRow[];
    totalHits: number;
    totalHitsCapped: boolean;
}

/**
 * Batch-resolve the owner's `verified` flag for project/component hits from the
 * public-tier accounts-view projection and join it server-side, so the badge
 * renders into the SSR HTML (no client-side per-card fetch).
 */
async function enrichProjectVerified(rows: SearchRow[], context: SiteSearchLoadContext): Promise<void> {
    const accountsView = context.accountsView;
    if (!accountsView) return;

    const ownerIds = Array.from(
        new Set(
            rows
                .filter((row): row is {kind: "project"; data: ProjectHit} => row.kind === "project")
                .map((row) => row.data.ownerUid)
                .filter(Boolean)
                .map((uid) => (uid.startsWith("usr_") ? uid : `usr_${uid}`)),
        ),
    );
    if (ownerIds.length === 0) return;

    try {
        const response = await typesenseSearch(
            accountsView.apiKey,
            accountsView.collection,
            {
                q: "*",
                query_by: "handle",
                filter_by: `id:[${ownerIds.join(",")}]`,
                include_fields: "id,verified",
                per_page: Math.min(ownerIds.length, 250),
            },
            accountsView.host,
        );

        const verifiedById = new Map<string, boolean>();
        for (const hit of response.hits) {
            const id = str(hit.document.id);
            if (id) verifiedById.set(id, bool(hit.document.verified));
        }

        for (const row of rows) {
            if (row.kind !== "project") continue;
            const id = row.data.ownerUid.startsWith("usr_") ? row.data.ownerUid : `usr_${row.data.ownerUid}`;
            row.data.ownerVerified = verifiedById.get(id) ?? false;
        }
    } catch (err) {
        // Enrichment is best-effort; never fail the whole render over a badge.
        // eslint-disable-next-line no-console
        console.error("owner verified enrichment failed:", err);
    }
}

/**
 * Server loader: runs in the SSR worker at the anonymous/public tier (the only
 * tier a bot/crawler sees). Returns `null` when there is no query or the worker
 * could not provide a scoped key, in which case the component falls back to the
 * client-side fetch.
 */
export async function loader({request, context}: LoaderFunctionArgs): Promise<SearchLoaderData | null> {
    const ctx = context as SiteSearchLoadContext;
    const url = new URL(request.url);
    const query = url.searchParams.get("q") ?? "";
    if (!query) return null;

    const queryType = (url.searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const page = parseInt(url.searchParams.get("page") ?? "1", 10) || 1;

    if (isAccountQuery(queryType)) {
        const accountsView = ctx.accountsView;
        if (!accountsView) return null;
        const response = await typesenseSearch(
            accountsView.apiKey,
            accountsView.collection,
            {
                q: query,
                query_by: "full_name,handle",
                sort_by: "follower_count:desc,_text_match:desc,star_count:desc",
                filter_by: queryType === "users" ? "accountType:=user" : "accountType:=organization",
                per_page: PER_PAGE,
                page,
                min_len_1typo: 4,
                min_len_2typo: 8,
            },
            accountsView.host,
        );
        const rows = response.hits
            .map((hit) => hit.document)
            .filter((doc) => keepAccount(doc, queryType as "users" | "orgs"))
            .map<SearchRow>((doc) => ({kind: "account", data: toAccountHit(doc)}));
        return {query, queryType, page, rows, totalHits: response.found, totalHitsCapped: false};
    }

    const projectType = queryType as "projects" | "components";
    if (!ctx.typesense) return null;
    const typesenseCredential = selectTypesenseSearchValue(ctx.typesense, projectType);
    if (page > maxUnscopedSearchPage(PER_PAGE)) {
        return {query, queryType, page, rows: [], totalHits: 0, totalHitsCapped: false};
    }
    const response = await typesenseSearch(
        typesenseCredential.apiKey,
        getProjectCollectionName(projectType),
        {
            q: query,
            query_by: getProjectQueryBy(projectType),
            sort_by: getProjectSortBy(projectType),
            filter_by: getProjectFilterBy(projectType),
            per_page: PER_PAGE,
            page: clampUnscopedSearchPage(page, PER_PAGE),
            limit_hits: UNSCOPED_SEARCH_RESULT_LIMIT,
            min_len_1typo: 5,
            min_len_2typo: 8,
        },
        typesenseCredential.host,
    );
    const rows = response.hits.flatMap<SearchRow>((hit) => {
        const data = toProjectHit(hit.document, projectType);
        return data ? [{kind: "project", data}] : [];
    });
    await enrichProjectVerified(rows, ctx);
    return {
        query,
        queryType,
        page,
        rows,
        totalHits: capUnscopedTotalHits(response.found),
        totalHitsCapped: isUnscopedTotalCapped(response.found),
    };
}

export default function SearchPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const query = searchParams.get("q") ?? "";
    const queryType = (searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const pageNum = parseInt(searchParams.get("page") ?? "1", 10);
    const explicitCategorySelection =
        (location.state as {explicitCategorySelection?: boolean} | null)?.explicitCategorySelection === true;

    const accountQuery = isAccountQuery(queryType);
    const projectClientInitialization = useTypesenseClientInitialization(Boolean(query));
    const projectClients = projectClientInitialization.client;
    const projectClient = projectClients ? selectTypesenseSearchValue(projectClients, queryType) : null;
    const {uid: viewerUid} = useViewer();
    // Tier-aware accounts-view client; re-resolves when the viewer's auth changes.
    const accountsViewInitialization = useAccountsViewClientInitialization(viewerUid);
    const accountsView = accountsViewInitialization.client;

    const humanizedType = humanizeType(queryType);

    // When served by the SSR worker the loader has already fetched (and
    // enriched) the results for this exact query; render them directly and skip
    // the client fetch. The federated host has no loader, so it fetches here.
    const loaderData = useLoaderData() as SearchLoaderData | null | undefined;
    const ssr =
        loaderData && loaderData.query === query && loaderData.queryType === queryType && loaderData.page === pageNum
            ? loaderData
            : null;

    const [clientRows, setClientRows] = useState<SearchRow[]>([]);
    const [clientTotalHits, setClientTotalHits] = useState(0);
    const [clientTotalHitsCapped, setClientTotalHitsCapped] = useState(false);
    const [loading, setLoading] = useState(false);
    const categoryCountKey = JSON.stringify([query, viewerUid ?? null]);
    const [categoryCountState, setCategoryCountState] = useState<{
        key: string;
        counts: Partial<Record<SearchIndexAlias, number>>;
    } | null>(null);
    const clientSearchKey = createClientSearchKey({
        queryType,
        query,
        page: pageNum,
        accountQuery,
        viewerUid,
    });
    const [resolvedClientSearchKey, setResolvedClientSearchKey] = useState<string | null>(null);
    const [failedClientSearchKey, setFailedClientSearchKey] = useState<string | null>(null);

    const rows = ssr ? ssr.rows : clientRows;
    const totalHits = ssr ? ssr.totalHits : clientTotalHits;
    const totalHitsCapped = ssr ? ssr.totalHitsCapped : clientTotalHitsCapped;
    const categoryCounts = categoryCountState?.key === categoryCountKey ? categoryCountState.counts : null;
    const initialSearchState = {
        hasServerResults: ssr !== null,
        accountQuery,
        accountsViewStatus: accountsViewInitialization.status,
        projectClientStatus: projectClientInitialization.status,
        hasResolvedClientSearch: resolvedClientSearchKey === clientSearchKey,
    };
    const waitingForClient = shouldShowInitialSearchSpinner(initialSearchState);
    const clientInitializationFailed = hasSearchClientFailed(initialSearchState);
    const currentSearchFailed = failedClientSearchKey === clientSearchKey;
    const currentSearchResolved = ssr !== null || resolvedClientSearchKey === clientSearchKey;
    const fallbackEligible = shouldUseSearchFallback({
        page: pageNum,
        totalHits,
        currentSearchResolved,
        currentSearchFailed,
        loading,
        clientInitializationFailed,
        explicitCategorySelection,
    });
    const categoryCountClientsFailed =
        projectClientInitialization.status === "error" || accountsViewInitialization.status === "error";
    const waitingForFallback = fallbackEligible && !categoryCountClientsFailed && categoryCounts === null;

    // Flag-gated virtualized listing (client-side only): spans the full result
    // set with on-demand page fetching instead of Previous/Next pagination.
    const {virtualization, customScrollbar, cardUnification} = useSearchListingConfig();
    const showVirtualizedResults =
        virtualization && totalHits > 0 && !clientInitializationFailed && !loading && !waitingForClient;
    useHideNativeScrollbar(customScrollbar && showVirtualizedResults);

    /** Fetches one page of results for the virtualized listing. */
    const fetchPage = useCallback(
        async (page: number): Promise<readonly SearchRow[]> => {
            if (accountQuery) {
                if (!accountsView) throw new Error("accounts search client unavailable");
                const data = await executeAccountSearch(
                    accountsView.client,
                    accountsView.collection,
                    queryType as "users" | "orgs",
                    query,
                    page,
                );
                return data.rows;
            }
            if (!projectClient) throw new Error("project search client unavailable");
            const data = await executeProjectSearch(projectClient, queryType as "projects" | "components", query, page);
            if (accountsView) {
                await enrichProjectVerifiedClient(data.rows, accountsView.client, accountsView.collection);
            }
            return data.rows;
        },
        [accountQuery, accountsView, projectClient, query, queryType],
    );

    const renderRow = useCallback(
        (row: SearchRow) =>
            row.kind === "account" ? (
                <AccountCard key={row.data.uid} account={row.data} />
            ) : (
                <ProjectCard key={row.data.uid} project={row.data} unified={cardUnification} uniformHeight />
            ),
        [cardUnification],
    );

    // Client-side fetch, keyed to the current location. The effect's cleanup
    // marks in-flight requests stale so a slow response for a previous
    // query/type/page can never overwrite a newer one, and previous results
    // are cleared up front so a failed replacement request doesn't reveal them.
    useEffect(() => {
        if (!query || ssr) return;

        let stale = false;

        const loadResults = async () => {
            if (accountQuery) {
                if (!accountsView) return;
                setClientRows([]);
                setClientTotalHits(0);
                setClientTotalHitsCapped(false);
                setFailedClientSearchKey(null);
                setLoading(true);
                try {
                    const data = await executeAccountSearch(
                        accountsView.client,
                        accountsView.collection,
                        queryType as "users" | "orgs",
                        query,
                        pageNum,
                    );
                    if (stale) return;
                    setClientRows(data.rows);
                    setClientTotalHits(data.totalHits);
                    setClientTotalHitsCapped(false);
                } catch (err) {
                    if (!stale) setFailedClientSearchKey(clientSearchKey);
                    // eslint-disable-next-line no-console
                    console.error("Search failed:", err);
                } finally {
                    if (!stale) {
                        setResolvedClientSearchKey(clientSearchKey);
                        setLoading(false);
                    }
                }
                return;
            }

            if (!projectClient) return;
            setClientRows([]);
            setClientTotalHits(0);
            setClientTotalHitsCapped(false);
            setFailedClientSearchKey(null);
            setLoading(true);
            try {
                const data = await executeProjectSearch(
                    projectClient,
                    queryType as "projects" | "components",
                    query,
                    pageNum,
                );
                if (accountsView) {
                    await enrichProjectVerifiedClient(data.rows, accountsView.client, accountsView.collection);
                }
                if (stale) return;
                setClientRows(data.rows);
                setClientTotalHits(data.totalHits);
                setClientTotalHitsCapped(data.totalHitsCapped);
            } catch (err) {
                if (!stale) setFailedClientSearchKey(clientSearchKey);
                // eslint-disable-next-line no-console
                console.error("Search failed:", err);
            } finally {
                if (!stale) {
                    setResolvedClientSearchKey(clientSearchKey);
                    setLoading(false);
                }
            }
        };

        void loadResults();

        return () => {
            stale = true;
        };
    }, [ssr, accountQuery, accountsView, projectClient, query, queryType, pageNum, clientSearchKey]);

    useEffect(() => {
        if (
            !query ||
            !projectClients ||
            !accountsView ||
            projectClientInitialization.status !== "ready" ||
            accountsViewInitialization.status !== "ready" ||
            categoryCountState?.key === categoryCountKey
        ) {
            return;
        }

        let stale = false;

        const loadCategoryCounts = async () => {
            try {
                const countResults = await Promise.allSettled(
                    SEARCH_INDEX_ALIASES.map((type) =>
                        executeSearchCount(projectClients, accountsView.client, accountsView.collection, type, query),
                    ),
                );
                if (stale) return;

                const counts: Partial<Record<SearchIndexAlias, number>> = {};
                for (const [index, type] of SEARCH_INDEX_ALIASES.entries()) {
                    const countResult = countResults[index];
                    if (countResult?.status === "fulfilled") {
                        counts[type] = countResult.value;
                    } else if (countResult?.status === "rejected") {
                        // eslint-disable-next-line no-console
                        console.error(`Search category count failed for ${type}:`, countResult.reason);
                    }
                }
                setCategoryCountState({key: categoryCountKey, counts});
            } catch (err) {
                if (stale) return;
                // eslint-disable-next-line no-console
                console.error("Search category counts failed:", err);
                setCategoryCountState({key: categoryCountKey, counts: {}});
            }
        };

        void loadCategoryCounts();

        return () => {
            stale = true;
        };
    }, [
        accountsView,
        accountsViewInitialization.status,
        categoryCountKey,
        categoryCountState?.key,
        projectClients,
        projectClientInitialization.status,
        query,
    ]);

    useEffect(() => {
        if (!fallbackEligible || categoryCounts === null) return;

        const fallbackType = getSearchFallbackType(queryType, {...categoryCounts, [queryType]: 0});
        if (fallbackType) {
            void navigate(createSearchUrl(query, fallbackType), {replace: true});
        }
    }, [categoryCounts, fallbackEligible, navigate, query, queryType]);

    const handleSearchSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const newQuery = formData.get("query") as string;
            if (newQuery) {
                void navigate(createSearchUrl(newQuery, queryType));
            }
        },
        [navigate, queryType],
    );

    const totalPages = Math.ceil(totalHits / PER_PAGE);

    useDocumentTitle(query ? `Flux Search Results for "${query}"` : "Flux Search");

    if (!query) {
        return (
            <Box sx={landingOuterSx}>
                <Box sx={{display: "inline-block", margin: "0 auto"}}>
                    <Container maxWidth="md">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Box sx={{marginBottom: "24px"}}>
                                <Box component="img" src={FluxLogo} alt="Flux logo" />
                            </Box>
                            <Box sx={{maxWidth: "522px"}}>
                                <Box component="form" onSubmit={handleSearchSubmit} aria-label="Search Flux">
                                    <TextField
                                        name="query"
                                        placeholder="Search Flux"
                                        fullWidth
                                        autoFocus
                                        variant="outlined"
                                        sx={{bgcolor: "rgba(33,33,33,0.8)"}}
                                    />
                                </Box>
                                <SearchTip />
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </Box>
        );
    }

    const absoluteUrl = toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&type=${queryType}`);

    return (
        <>
            <Container maxWidth="md">
                <Box sx={{mb: 2}}>
                    <Breadcrumbs aria-label="Breadcrumb">
                        <Link component={RouterLink} to={Routes.SEARCH} color="inherit" underline="hover">
                            Search
                        </Link>
                        <Link href={absoluteUrl} color="inherit" underline="hover">
                            &quot;{query}&quot; in {humanizedType}
                            {totalHits > 0 &&
                                (totalHitsCapped
                                    ? ` (first ${UNSCOPED_SEARCH_RESULT_LIMIT_LABEL} of many results)`
                                    : ` (${totalHits} results)`)}
                        </Link>
                    </Breadcrumbs>
                </Box>

                <Grid container spacing={2}>
                    <Grid component="aside" size={{xs: 12, lg: 3}}>
                        <Paper square>
                            <Box sx={{p: 1.5}}>
                                <Typography variant="body1" sx={{mb: 1}}>
                                    Filter by
                                </Typography>
                                <List disablePadding>
                                    {SEARCH_INDEX_ALIASES.map((type) => (
                                        <li key={type}>
                                            <Link
                                                component={RouterLink}
                                                to={createSearchUrl(query, type)}
                                                state={{explicitCategorySelection: true}}
                                                color={queryType === type ? "primary" : "text.secondary"}
                                                underline="hover"
                                                variant="body2"
                                                aria-current={queryType === type ? "page" : undefined}
                                            >
                                                {humanizeType(type)}
                                                {categoryCounts?.[type] !== undefined
                                                    ? ` (${formatSearchSectionCount(
                                                          categoryCounts[type],
                                                          UNSCOPED_SEARCH_RESULT_LIMIT,
                                                      )})`
                                                    : ""}
                                            </Link>
                                        </li>
                                    ))}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid component="main" size={{xs: 12, lg: 9}} aria-live="polite">
                        {clientInitializationFailed ? (
                            <Box sx={{p: 1.5}} role="alert">
                                <Typography variant="h6" component="h2">
                                    Search is temporarily unavailable.
                                </Typography>
                            </Box>
                        ) : loading || waitingForClient || waitingForFallback ? (
                            <Box
                                aria-busy={true}
                                aria-label="Search results loading"
                                role="status"
                                sx={{display: "flex", justifyContent: "center", py: 8}}
                            >
                                <CircularProgress aria-label="Loading search results" />
                            </Box>
                        ) : showVirtualizedResults ? (
                            <VirtualizedSearchResults
                                key={`${queryType}:${query}`}
                                totalHits={totalHits}
                                perPage={PER_PAGE}
                                initialPage={pageNum}
                                initialRows={rows}
                                fetchPage={fetchPage}
                                renderRow={renderRow}
                                renderSkeleton={
                                    !accountQuery && cardUnification ? renderUnifiedProjectSkeleton : undefined
                                }
                                estimatedRowHeightPx={
                                    !accountQuery && cardUnification ? UNIFORM_PROJECT_CARD_ROW_HEIGHT_PX : undefined
                                }
                                rowGapPx={!accountQuery && cardUnification ? PROJECT_CARD_ROW_GAP_PX : undefined}
                                customScrollbar={customScrollbar}
                            />
                        ) : (
                            <>
                                <List aria-label={humanizedType} disablePadding>
                                    {rows.map((row) =>
                                        row.kind === "account" ? (
                                            <AccountCard key={row.data.uid} account={row.data} />
                                        ) : (
                                            <ProjectCard
                                                key={row.data.uid}
                                                project={row.data}
                                                unified={cardUnification}
                                            />
                                        ),
                                    )}
                                </List>

                                {rows.length === 0 && (
                                    <Box sx={{p: 1.5}} role="status">
                                        <Typography variant="h6" component="h2">
                                            No results found for &quot;{query}&quot;
                                        </Typography>
                                    </Box>
                                )}

                                {totalPages > 1 && (
                                    <Box sx={{display: "flex", justifyContent: "center", gap: 2, py: 4}}>
                                        {pageNum > 1 && (
                                            <Link
                                                component={RouterLink}
                                                to={`${Routes.SEARCH}?q=${encodeURIComponent(query)}&type=${queryType}&page=${pageNum - 1}`}
                                            >
                                                Previous
                                            </Link>
                                        )}
                                        <Typography color="text.secondary" variant="body2">
                                            Page {pageNum} of {totalPages}
                                        </Typography>
                                        {pageNum < totalPages && (
                                            <Link
                                                component={RouterLink}
                                                to={`${Routes.SEARCH}?q=${encodeURIComponent(query)}&type=${queryType}&page=${pageNum + 1}`}
                                            >
                                                Next
                                            </Link>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
